import React, { useState, useEffect, useMemo } from 'react';
import { alpha } from '@mui/material/styles';
import {
  Box,
  Button,
  Paper,
  Grid,
  IconButton,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  FormHelperText,
  CircularProgress,
  Drawer,
  FormControlLabel,
  Switch,
  Alert,
  Snackbar,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import CloseIcon from '@mui/icons-material/Close';
import ShareIcon from '@mui/icons-material/Share';
import { detectThemeSync, watchThemeChanges } from '../../utils/themeUtils';
import {
  getDistributionSectionPaperSx,
  getDistributionSectionHeaderRowSx,
  getDistributionSectionHeaderLeadSx,
  getDistributionSectionIconBoxSx,
  getDistributionSectionIconFontSize,
  getDistributionSectionTitleSx,
  getDistributionSectionSubtitleSx,
  getDistributionActionButtonSx,
  isDistributionMobileLayout,
} from '../../utils/distributionMobileLayout';
import {
  getBookingDrawerTheme,
  getBookingDrawerPaperSx,
  getBookingDrawerHeaderSx,
  getBookingDrawerFooterSx,
  getGuestDrawerContentSx,
  getGuestDrawerFieldSx,
  getGuestDrawerSelectSx,
  getBookingDrawerOutlinedButtonSx,
  getBookingDrawerPrimaryButtonSx,
  BOOKING_DRAWER_ACCENT,
} from '../../utils/bookingDrawerLayout';
import {
  createDrawerOverlayProps,
  getDrawerRootSx,
} from '../../utils/drawerOverlayProps';
import ChannelSetupCard from './ChannelSetupCard';
import {
  fetchAllChannels,
  createChannel,
  updateChannel,
  deleteChannel,
  fetchOTAImages,
} from '../../services/channelService';

const THEME_COLOR = '#4ce1b6';

const CHANNEL_TYPES = [
  { value: 'OTA', label: 'OTA' },
  { value: 'PMS', label: 'PMS' },
  { value: 'DIRECT', label: 'Direct Booking' },
  { value: 'CHANNEL_MANAGER', label: 'Channel Manager' },
];

const UPDATE_MODES = [
  { value: 'INSTANT', label: 'Instant' },
  { value: 'SCHEDULED', label: 'Scheduled' },
  { value: 'MANUAL', label: 'Manual' },
];

const ChannelSetupSection = () => {
  const theme = useTheme();
  const [currentTheme, setCurrentTheme] = useState(() => detectThemeSync());
  const isDark = currentTheme === 'dark';
  const isMobileLayout = isDistributionMobileLayout();
  const drawerTheme = getBookingDrawerTheme(isDark);
  const drawerOverlays = useMemo(
    () => createDrawerOverlayProps(isDark, isDark ? drawerTheme.surface : '#ffffff'),
    [isDark, drawerTheme.surface]
  );

  // Watch for theme changes
  useEffect(() => {
    const cleanup = watchThemeChanges((newTheme) => {
      setCurrentTheme(newTheme);
    });
    return cleanup;
  }, []);

  const [channels, setChannels] = useState([]);
  const [loading, setLoading] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deleteDialogError, setDeleteDialogError] = useState('');
  const [editingChannel, setEditingChannel] = useState(null);
  const [channelToDelete, setChannelToDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [formData, setFormData] = useState({
    name: '',
    channel_type: '',
    hotel_id: '',
    image_path: '',
    enabled: true,
    update_mode: 'INSTANT',
    rate_limit_per_minute: 60,
    config: {},
  });
  const [errors, setErrors] = useState({});
  const [otaImages, setOtaImages] = useState([]);
  const [loadingImages, setLoadingImages] = useState(false);

  const getApiErrorMessage = (error, fallback) => {
    const data = error?.response?.data || {};
    if (data.error) return data.error;
    if (data.message) return data.message;
    if (Array.isArray(data.errors?.__all__) && data.errors.__all__.length) {
      return data.errors.__all__.join(', ');
    }
    return fallback;
  };

  const getChannelDependencyCounts = (channel) => ({
    inventoryCount: Number(channel?.inventory_count || 0),
    mappingCount: Number(channel?.mapping_count || 0),
  });

  const isChannelDeletable = (channel) => {
    const { inventoryCount, mappingCount } = getChannelDependencyCounts(channel);
    return inventoryCount === 0 && mappingCount === 0;
  };

  const getChannelDeleteTooltip = (channel) => {
    const { inventoryCount, mappingCount } = getChannelDependencyCounts(channel);
    if (inventoryCount === 0 && mappingCount === 0) {
      return 'Delete channel';
    }
    const parts = [];
    if (inventoryCount > 0) parts.push(`${inventoryCount} inventory allocation(s)`);
    if (mappingCount > 0) parts.push(`${mappingCount} mapping(s)`);
    return `Delete blocked: remove ${parts.join(' and ')} first.`;
  };

  useEffect(() => {
    fetchChannels();
    fetchImages();
  }, []);

  const fetchImages = async () => {
    setLoadingImages(true);
    try {
      const data = await fetchOTAImages();
      setOtaImages(data.images || []);
    } catch (error) {} finally {
      setLoadingImages(false);
    }
  };

  const fetchChannels = async () => {
    setLoading(true);
    try {
      const data = await fetchAllChannels();
      setChannels(data.channels || []);
    } catch (error) {setSnackbar({
        open: true,
        message: 'Failed to load channels. Please try again.',
        severity: 'error',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleOpenDrawer = (channel = null) => {
    if (channel) {
      setEditingChannel(channel);
      setFormData({
        name: channel.name || '',
        channel_type: channel.channel_type || '',
        hotel_id: channel.hotel_id || channel.channel_hotel_id || '',
        image_path: channel.image_path || '',
        enabled: channel.enabled !== undefined ? channel.enabled : true,
        update_mode: channel.update_mode || 'INSTANT',
        rate_limit_per_minute: channel.rate_limit_per_minute || 60,
        config: channel.config || {},
      });
    } else {
      setEditingChannel(null);
      setFormData({
        name: '',
        channel_type: '',
        hotel_id: '',
        image_path: '',
        enabled: true,
        update_mode: 'INSTANT',
        rate_limit_per_minute: 60,
        config: {},
      });
    }
    setErrors({});
    setDrawerOpen(true);
  };

  const handleCloseDrawer = () => {
    setDrawerOpen(false);
    setEditingChannel(null);
    setErrors({});
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (errors[field]) {
      setErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[field];
        return newErrors;
      });
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Channel name is required';
    }
    
    if (!formData.channel_type) {
      newErrors.channel_type = 'Channel type is required';
    }
    
    if (formData.rate_limit_per_minute < 1) {
      newErrors.rate_limit_per_minute = 'Rate limit must be at least 1';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      if (editingChannel) {
        await updateChannel(editingChannel.id, formData);
        setSnackbar({
          open: true,
          message: 'Channel updated successfully',
          severity: 'success',
        });
      } else {
        await createChannel(formData);
        setSnackbar({
          open: true,
          message: 'Channel created successfully',
          severity: 'success',
        });
      }
      handleCloseDrawer();
      fetchChannels();
    } catch (error) {if (error.response?.data?.errors) {
        setErrors(error.response.data.errors);
      } else {
        setSnackbar({
          open: true,
          message: error.response?.data?.error || 'Failed to save channel. Please try again.',
          severity: 'error',
        });
      }
    }
  };

  const handleDeleteClick = (channel) => {
    if (!isChannelDeletable(channel)) {
      setSnackbar({
        open: true,
        message: getChannelDeleteTooltip(channel),
        severity: 'warning',
      });
      return;
    }
    setDeleteDialogError('');
    setChannelToDelete(channel);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!channelToDelete) return;

    setDeletingId(channelToDelete.id);
    try {
      await deleteChannel(channelToDelete.id);
      setSnackbar({
        open: true,
        message: 'Channel deleted successfully',
        severity: 'success',
      });
      setDeleteDialogOpen(false);
      setDeleteDialogError('');
      setChannelToDelete(null);
      fetchChannels();
    } catch (error) {
      const errorMessage = getApiErrorMessage(
        error,
        'Failed to delete channel. Please remove related inventory/mappings and try again.'
      );
      setDeleteDialogError(errorMessage);
      setSnackbar({
        open: true,
        message: errorMessage,
        severity: 'error',
      });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <Box>
      <Paper
        elevation={0}
        sx={getDistributionSectionPaperSx(theme, isMobileLayout)}
      >
        <Box sx={getDistributionSectionHeaderRowSx(isMobileLayout)}>
          <Box sx={getDistributionSectionHeaderLeadSx(isMobileLayout)}>
            <Box sx={getDistributionSectionIconBoxSx(THEME_COLOR, isMobileLayout)}>
              <ShareIcon sx={{ fontSize: getDistributionSectionIconFontSize(isMobileLayout), color: THEME_COLOR }} />
            </Box>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h5" sx={getDistributionSectionTitleSx(isMobileLayout)}>
                Channel Setup
              </Typography>
              <Typography variant="body2" sx={getDistributionSectionSubtitleSx(isMobileLayout)}>
                Configure and manage your distribution channels
              </Typography>
            </Box>
          </Box>
          <Button
            variant="contained"
            startIcon={<AddIcon />}
            onClick={() => handleOpenDrawer()}
            sx={getDistributionActionButtonSx(THEME_COLOR, isMobileLayout)}
          >
            Add Channel
          </Button>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
            <CircularProgress />
          </Box>
        ) : channels.length === 0 ? (
          <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
            No channels found. Click "Add Channel" to create one.
          </Typography>
        ) : (
          <Grid container spacing={2.5} sx={{ '& .MuiGrid-item': { display: 'flex' } }}>
            {channels.map((channel) => (
              <Grid item xs={12} sm={6} lg={4} key={channel.id} sx={{ display: 'flex' }}>
                <ChannelSetupCard
                  channel={channel}
                  isDark={isDark}
                  onEdit={() => handleOpenDrawer(channel)}
                  onDelete={() => handleDeleteClick(channel)}
                  deleteDisabled={!isChannelDeletable(channel)}
                  deleteTooltip={getChannelDeleteTooltip(channel)}
                />
              </Grid>
            ))}
          </Grid>
        )}
      </Paper>

      {/* Channel Drawer */}
      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={handleCloseDrawer}
        sx={getDrawerRootSx()}
        PaperProps={{
          sx: getBookingDrawerPaperSx(isDark, {
            width: { xs: '100%', sm: 600 },
            display: 'flex',
            flexDirection: 'column',
            height: '100%',
          }),
        }}
      >
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', position: 'relative' }}>
          <Box
            sx={{
              ...getBookingDrawerHeaderSx(isDark),
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              p: 1.5,
              flexShrink: 0,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
              <Box sx={getDistributionSectionIconBoxSx(THEME_COLOR, isMobileLayout)}>
                <ShareIcon sx={{ fontSize: 22, color: THEME_COLOR }} />
              </Box>
              <Typography variant="h6" sx={{ color: drawerTheme.text, fontWeight: 700, fontSize: '1.05rem' }}>
                {editingChannel ? 'Edit Channel' : 'Add Channel'}
              </Typography>
            </Box>
            <IconButton
              onClick={handleCloseDrawer}
              size="small"
              sx={{
                color: drawerTheme.subtleText,
                '&:hover': { backgroundColor: isDark ? alpha('#ffffff', 0.08) : alpha('#000', 0.04) },
              }}
            >
              <CloseIcon />
            </IconButton>
          </Box>

          <Box
            sx={{
              flex: 1,
              overflowY: 'auto',
              p: 2,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
              ...getGuestDrawerContentSx(isDark),
            }}
          >
            <TextField
              label="Channel Name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              error={!!errors.name}
              helperText={errors.name}
              required
              fullWidth
              sx={getGuestDrawerFieldSx(isDark, !!errors.name)}
            />

            <TextField
              label="Hotel ID"
              value={formData.hotel_id}
              onChange={(e) => handleInputChange('hotel_id', e.target.value)}
              error={!!errors.hotel_id}
              helperText={errors.hotel_id || 'Hotel ID in the channel system (can be number or string)'}
              fullWidth
              placeholder="Enter hotel ID"
              sx={getGuestDrawerFieldSx(isDark, !!errors.hotel_id)}
            />

            <FormControl fullWidth error={!!errors.image_path}>
              <InputLabel>Channel Logo</InputLabel>
              <Select
                value={formData.image_path || ''}
                onChange={(e) => handleInputChange('image_path', e.target.value)}
                label="Channel Logo"
                sx={getGuestDrawerSelectSx(isDark, !!errors.image_path)}
                MenuProps={drawerOverlays.selectMenuProps}
                renderValue={(value) => {
                  if (!value) return 'None';
                  const image = otaImages.find((img) => img.path === value);
                  return image ? image.filename : value;
                }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {otaImages.map((image) => (
                  <MenuItem key={image.path} value={image.path}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <img
                        src={image.url}
                        alt={image.filename}
                        style={{ width: 24, height: 24, objectFit: 'contain' }}
                        onError={(e) => {
                          e.target.style.display = 'none';
                        }}
                      />
                      <Typography>{image.filename}</Typography>
                    </Box>
                  </MenuItem>
                ))}
              </Select>
              {errors.image_path && <FormHelperText>{errors.image_path}</FormHelperText>}
            </FormControl>

            <FormControl fullWidth error={!!errors.channel_type} required>
              <InputLabel>Channel Type</InputLabel>
              <Select
                value={formData.channel_type}
                onChange={(e) => handleInputChange('channel_type', e.target.value)}
                label="Channel Type"
                sx={getGuestDrawerSelectSx(isDark, !!errors.channel_type)}
                MenuProps={drawerOverlays.selectMenuProps}
              >
                {CHANNEL_TYPES.map((type) => (
                  <MenuItem key={type.value} value={type.value}>
                    {type.label}
                  </MenuItem>
                ))}
              </Select>
              {errors.channel_type && <FormHelperText>{errors.channel_type}</FormHelperText>}
            </FormControl>

            <FormControl fullWidth>
              <InputLabel>Update Mode</InputLabel>
              <Select
                value={formData.update_mode}
                onChange={(e) => handleInputChange('update_mode', e.target.value)}
                label="Update Mode"
                sx={getGuestDrawerSelectSx(isDark)}
                MenuProps={drawerOverlays.selectMenuProps}
              >
                {UPDATE_MODES.map((mode) => (
                  <MenuItem key={mode.value} value={mode.value}>
                    {mode.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            <TextField
              label="Rate Limit Per Minute"
              type="number"
              value={formData.rate_limit_per_minute}
              onChange={(e) => handleInputChange('rate_limit_per_minute', parseInt(e.target.value, 10) || 60)}
              error={!!errors.rate_limit_per_minute}
              helperText={errors.rate_limit_per_minute}
              fullWidth
              inputProps={{ min: 1 }}
              sx={getGuestDrawerFieldSx(isDark, !!errors.rate_limit_per_minute)}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.enabled}
                  onChange={(e) => handleInputChange('enabled', e.target.checked)}
                  sx={{
                    '& .MuiSwitch-switchBase.Mui-checked': { color: BOOKING_DRAWER_ACCENT },
                    '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': {
                      backgroundColor: alpha(BOOKING_DRAWER_ACCENT, 0.45),
                    },
                  }}
                />
              }
              label="Active"
              sx={{ color: drawerTheme.text, ml: 0.25 }}
            />
          </Box>

          <Box sx={{ ...getBookingDrawerFooterSx(isDark), p: 1.5, flexShrink: 0 }}>
            <Grid container spacing={1.5}>
              <Grid item xs={6}>
                <Button
                  variant="outlined"
                  fullWidth
                  onClick={handleCloseDrawer}
                  sx={getBookingDrawerOutlinedButtonSx(isDark)}
                >
                  Cancel
                </Button>
              </Grid>
              <Grid item xs={6}>
                <Button
                  variant="contained"
                  fullWidth
                  onClick={handleSave}
                  sx={getBookingDrawerPrimaryButtonSx(isDark)}
                >
                  {editingChannel ? 'Update' : 'Create'}
                </Button>
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Drawer>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          setDeleteDialogOpen(false);
          setDeleteDialogError('');
        }}
      >
        <DialogTitle>Delete Channel</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Are you sure you want to delete the channel "{channelToDelete?.name}"? This action cannot be undone.
            Channel deletion is allowed only when no channel inventory or mappings are associated.
          </DialogContentText>
          {deleteDialogError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {deleteDialogError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setDeleteDialogError('');
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteConfirm}
            color="error"
            variant="contained"
            disabled={deletingId === channelToDelete?.id}
          >
            {deletingId === channelToDelete?.id ? <CircularProgress size={20} /> : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ChannelSetupSection;

